import { deleteDestination } from '@gonfalon/openapi/operations/deleteDestination';
import { getDestination } from '@gonfalon/openapi/operations/getDestination';
import { getDestinations } from '@gonfalon/openapi/operations/getDestinations';
import { patchDestination } from '@gonfalon/openapi/operations/patchDestination';
import { postDestination } from '@gonfalon/openapi/operations/postDestination';
import { postDestinationTestEvent } from '@gonfalon/openapi/operations/postDestinationTestEvent';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getDestinationsQuery = createQueryOptions(getDestinations);

export const getDestinationQuery = createQueryOptions(getDestination);

/**
 * Exported for the legacy Redux write path in `#actions/integrations` and `#actions/webhooks`, which
 * still owns these mutations while the integrations page is mid-migration (REL-15378). The Redux
 * page reads its own store, so without this the react-query page shows stale data for the length of
 * `staleTime` after any create, edit or delete.
 */
export async function invalidateDestinations() {
  const queryClient = getQueryClient();
  await queryClient.invalidateQueries({ queryKey: getDestinationsQuery.partialQueryKey() });
}

export const useCreateDestination = createMutationHook(postDestination, {
  onSuccess: invalidateDestinations,
});

export const useUpdateDestination = createMutationHook(patchDestination, {
  onSuccess: async (_data, { projectKey, environmentKey, id }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getDestinationQuery.partialQueryKey({ projectKey, environmentKey, id }),
    });
    await invalidateDestinations();
  },
});

export const useDeleteDestination = createMutationHook(deleteDestination, {
  onSuccess: async (_data, { projectKey, environmentKey, id }) => {
    const queryClient = getQueryClient();
    queryClient.removeQueries({
      queryKey: getDestinationQuery.partialQueryKey({ projectKey, environmentKey, id }),
    });
    await invalidateDestinations();
  },
});

export const useSendDestinationTestEvent = createMutationHook(postDestinationTestEvent);
